import {
  Search,
  Filter,
  ChevronDown,
  X,
  Loader2,
  SlidersHorizontal,
} from 'lucide-react';
import TagPicker from './TagPicker';
import type { ForumListItem, ProfileSearchFilters, UserProfileListItem } from '../types';

export const US_STATES = [
  'Alabama', 'Alaska', 'Arizona', 'Arkansas', 'California', 'Colorado', 'Connecticut',
  'Delaware', 'Florida', 'Georgia', 'Hawaii', 'Idaho', 'Illinois', 'Indiana', 'Iowa',
  'Kansas', 'Kentucky', 'Louisiana', 'Maine', 'Maryland', 'Massachusetts', 'Michigan',
  'Minnesota', 'Mississippi', 'Missouri', 'Montana', 'Nebraska', 'Nevada', 'New Hampshire',
  'New Jersey', 'New Mexico', 'New York', 'North Carolina', 'North Dakota', 'Ohio',
  'Oklahoma', 'Oregon', 'Pennsylvania', 'Rhode Island', 'South Carolina', 'South Dakota',
  'Tennessee', 'Texas', 'Utah', 'Vermont', 'Virginia', 'Washington', 'West Virginia',
  'Wisconsin', 'Wyoming',
];

export const USER_TYPE_OPTIONS = [
  { value: 'member', label: 'Members' },
  { value: 'advisor', label: 'Advisors' },
  { value: 'facilitator', label: 'Facilitators' },
  { value: 'admin', label: 'Admins' },
];

export interface DirectoryFilterState {
  state: string;
  forumId: number | null;
  industry: string;
  userType: string;
  expertiseTags: string[];
  growthTags: string[];
  familyStatus: string;
  hasChildren: boolean | '';
  minYears: string;
  maxYears: string;
}

export const EMPTY_DIRECTORY_FILTERS: DirectoryFilterState = {
  state: '',
  forumId: null,
  industry: '',
  userType: '',
  expertiseTags: [],
  growthTags: [],
  familyStatus: '',
  hasChildren: '',
  minYears: '',
  maxYears: '',
};

export function countDirectoryFilters(filters: DirectoryFilterState): number {
  return [
    filters.state,
    filters.forumId,
    filters.industry,
    filters.userType,
    filters.expertiseTags.length > 0 ? 'tags' : '',
    filters.growthTags.length > 0 ? 'growth' : '',
    filters.familyStatus,
    filters.hasChildren !== '' ? 'children' : '',
    filters.minYears,
    filters.maxYears,
  ].filter(Boolean).length;
}

export function toProfileSearchFilters(filters: DirectoryFilterState): ProfileSearchFilters {
  const serverFilters: ProfileSearchFilters = {};
  if (filters.state) serverFilters.state = filters.state;
  if (filters.forumId) serverFilters.forum_id = filters.forumId;
  if (filters.industry) serverFilters.industry = filters.industry;
  if (filters.userType) serverFilters.role = filters.userType;
  if (filters.familyStatus) serverFilters.family_status = filters.familyStatus;
  if (filters.hasChildren !== '') serverFilters.has_children = filters.hasChildren;
  if (filters.minYears) serverFilters.min_years_in_business = Number(filters.minYears);
  if (filters.maxYears) serverFilters.max_years_in_business = Number(filters.maxYears);
  return serverFilters;
}

export function applyDirectoryTagFilters(
  members: UserProfileListItem[],
  filters: DirectoryFilterState,
): UserProfileListItem[] {
  let result = members;
  if (filters.expertiseTags.length > 0) {
    result = result.filter((m) =>
      filters.expertiseTags.some((tag) =>
        m.areas_of_expertise?.some((e) => e.toLowerCase().includes(tag.toLowerCase())),
      ),
    );
  }
  if (filters.growthTags.length > 0) {
    result = result.filter((m) =>
      filters.growthTags.some((tag) =>
        m.growth_areas?.some((g) => g.toLowerCase().includes(tag.toLowerCase())),
      ),
    );
  }
  return result;
}

interface DirectorySearchFiltersProps {
  searchQuery: string;
  onSearchChange: (value: string) => void;
  isSearching?: boolean;
  filters: DirectoryFilterState;
  onFiltersChange: (filters: DirectoryFilterState) => void;
  showFilters: boolean;
  onToggleFilters: () => void;
  showAdvanced: boolean;
  onToggleAdvanced: () => void;
  forums: ForumListItem[];
  industries: string[];
  /** Switches to the Ask IF tab (renders the "advanced search" hint when set). */
  onAskIf?: () => void;
}

export default function DirectorySearchFilters({
  searchQuery,
  onSearchChange,
  isSearching = false,
  filters,
  onFiltersChange,
  showFilters,
  onToggleFilters,
  showAdvanced,
  onToggleAdvanced,
  forums,
  industries,
  onAskIf,
}: DirectorySearchFiltersProps) {
  const activeFilterCount = countDirectoryFilters(filters);
  const setFilter = <K extends keyof DirectoryFilterState>(
    key: K,
    value: DirectoryFilterState[K],
  ) => onFiltersChange({ ...filters, [key]: value });

  return (
    <div>
      <div className="flex flex-col md:flex-row gap-4">
        <div className="flex-1 min-w-0">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" size={20} />
            <input
              type="text"
              placeholder="Search by keyword: name, industry, hobby, expertise..."
              value={searchQuery}
              onChange={(e) => onSearchChange(e.target.value)}
              className="w-full pl-10 pr-10 py-2.5 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-navy/20"
            />
            {isSearching && (
              <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 animate-spin" size={18} />
            )}
          </div>
          <p className="mt-2 text-xs text-gray-500 leading-relaxed">
            Keyword search matches exact words in member profiles.
            {onAskIf && (
              <>
                {' '}For a smarter search, describe who you&apos;re looking for in{' '}
                <button
                  type="button"
                  onClick={onAskIf}
                  className="font-medium text-navy hover:underline"
                >
                  Ask IF
                </button>
                .
              </>
            )}
          </p>
        </div>

        <button
          type="button"
          onClick={onToggleFilters}
          aria-expanded={showFilters}
          className={`flex items-center gap-2 px-4 py-2.5 border rounded-lg transition ${
            showFilters || activeFilterCount > 0
              ? 'border-navy bg-navy text-white'
              : 'border-gray-200 text-gray-700 hover:bg-gray-50'
          }`}
        >
          <Filter size={18} />
          <span>Filters</span>
          {activeFilterCount > 0 && (
            <span className="bg-gold text-white text-xs px-2 py-0.5 rounded-full">
              {activeFilterCount}
            </span>
          )}
          <ChevronDown size={16} className={`transition ${showFilters ? 'rotate-180' : ''}`} />
        </button>
      </div>

      {showFilters && (
        <div className="mt-4 pt-4 border-t border-gray-100 space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">State</label>
              <select
                value={filters.state}
                onChange={(e) => setFilter('state', e.target.value)}
                className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-navy/20"
              >
                <option value="">All States</option>
                {US_STATES.map((state) => (
                  <option key={state} value={state}>{state}</option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Forum</label>
              <select
                value={filters.forumId || ''}
                onChange={(e) =>
                  setFilter('forumId', e.target.value ? parseInt(e.target.value, 10) : null)
                }
                className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-navy/20"
              >
                <option value="">All Forums</option>
                {forums.map((forum) => (
                  <option key={forum.id} value={forum.id}>
                    {forum.name} ({forum.city}, {forum.state})
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Industry</label>
              <select
                value={filters.industry}
                onChange={(e) => setFilter('industry', e.target.value)}
                className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-navy/20"
              >
                <option value="">All Industries</option>
                {industries.map((industry) => (
                  <option key={industry} value={industry}>{industry}</option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">User Type</label>
              <select
                value={filters.userType}
                onChange={(e) => setFilter('userType', e.target.value)}
                className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-navy/20"
              >
                <option value="">All Types</option>
                {USER_TYPE_OPTIONS.map((opt) => (
                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
              </select>
            </div>
          </div>

          <button
            type="button"
            onClick={onToggleAdvanced}
            aria-expanded={showAdvanced}
            className="flex items-center gap-1.5 text-sm text-navy hover:underline"
          >
            <SlidersHorizontal size={14} />
            {showAdvanced ? 'Hide' : 'Show'} Advanced Filters
            <ChevronDown size={14} className={`transition ${showAdvanced ? 'rotate-180' : ''}`} />
          </button>

          {showAdvanced && (
            <div className="space-y-4 pt-2 border-t border-gray-50">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Expertise</label>
                <TagPicker
                  selectedTags={filters.expertiseTags}
                  onChange={(tags) => setFilter('expertiseTags', tags)}
                  placeholder="Filter by expertise..."
                  accentColor="navy"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Growth Areas</label>
                <TagPicker
                  selectedTags={filters.growthTags}
                  onChange={(tags) => setFilter('growthTags', tags)}
                  placeholder="Filter by growth areas..."
                  accentColor="gold"
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Years in Business
                  </label>
                  <div className="flex items-center gap-2">
                    <input
                      type="number"
                      value={filters.minYears}
                      onChange={(e) => setFilter('minYears', e.target.value)}
                      placeholder="Min"
                      min={0}
                      className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-navy/20"
                    />
                    <span className="text-gray-400">-</span>
                    <input
                      type="number"
                      value={filters.maxYears}
                      onChange={(e) => setFilter('maxYears', e.target.value)}
                      placeholder="Max"
                      min={0}
                      className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-navy/20"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Family Status</label>
                  <select
                    value={filters.familyStatus}
                    onChange={(e) => setFilter('familyStatus', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-navy/20"
                  >
                    <option value="">Any</option>
                    <option value="single">Single</option>
                    <option value="married">Married</option>
                    <option value="divorced">Divorced</option>
                    <option value="widowed">Widowed</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Has Children</label>
                  <select
                    value={String(filters.hasChildren)}
                    onChange={(e) =>
                      setFilter(
                        'hasChildren',
                        e.target.value === '' ? '' : e.target.value === 'true',
                      )
                    }
                    className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-navy/20"
                  >
                    <option value="">Any</option>
                    <option value="true">Yes</option>
                    <option value="false">No</option>
                  </select>
                </div>
              </div>
            </div>
          )}

          {activeFilterCount > 0 && (
            <button
              type="button"
              onClick={() => onFiltersChange(EMPTY_DIRECTORY_FILTERS)}
              className="flex items-center gap-1 text-sm text-gray-500 hover:text-gray-700"
            >
              <X size={14} />
              Clear all filters
            </button>
          )}
        </div>
      )}
    </div>
  );
}
